'use client';

import { Root as RadixRoot } from '@radix-ui/react-checkbox';
import * as RadixForm from '@radix-ui/react-form';
import {
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { Checkbox } from '../Checkbox';
import { InputFieldError } from '../InputField/InputFieldError';

import { CheckboxFieldError } from './CheckboxFieldError';
import { CheckboxFieldLabel } from './CheckboxFieldLabel';
import { CheckboxFieldNote } from './CheckboxFieldNote';

import type { CheckboxProps } from '../Checkbox';
import type {
  ForwardRefRenderFunction,
  ReactNode} from 'react';

export type CheckboxFieldProps = Omit<CheckboxProps, 'required'> & {
  label?: ReactNode;
  note?: ReactNode;
  error?: string;

  asInline?: boolean;

  /**
   * Use `label` if you don't want to add the `required` attribute to the input element itself.
   * This will avoid highlighting the input as invalid, when there is no value.
   * This is usually a user friendlier way than highlighting the field invalid on initial page load.
   */
  required?: boolean | 'label';

  asRadix?: boolean;
  radixFormMessages?: RadixForm.FormMessageProps[];
};

const CheckboxFieldRenderFunction: ForwardRefRenderFunction<
  HTMLInputElement | null,
  CheckboxFieldProps
> = (
  {
    label,
    note,
    error,
    required,
    asRadix,
    radixFormMessages,
    asInline,
    ...attrs
  },
  forwardedRef,
) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle<HTMLInputElement | null, HTMLInputElement | null>(
    forwardedRef,
    () => inputRef.current,
  );

  useEffect(() => {
    if (asRadix) {
      return;
    }

    inputRef.current?.setCustomValidity(error ?? '');
  }, [asRadix, error]);

  if (!asRadix) {
    return (
      <div className="space-y-1">
        <label
          className={twMerge(
            'flex cursor-pointer gap-2',
            asInline && 'inline-flex',
          )}
        >
          <Checkbox
            {...attrs}
            required={typeof required === 'boolean' ? required : undefined}
            ref={inputRef}
          />

          {(label || note) && (
            <div>
              {label && (
                <CheckboxFieldLabel asChild>
                  <div>
                    {label}
                    {required ? '*' : ''}
                  </div>
                </CheckboxFieldLabel>
              )}
              {note && (
                <CheckboxFieldNote>
                  {note}
                  {!label && required ? '*' : ''}
                </CheckboxFieldNote>
              )}
            </div>
          )}
        </label>

        {error && <CheckboxFieldError>{error}</CheckboxFieldError>}
      </div>
    );
  }

  return (
    <RadixForm.Field name={attrs.name ?? ''} asChild>
      <div className="space-y-1">
        <label className="flex cursor-pointer gap-2">
          <RadixForm.Control asChild>
            <RadixRoot asChild>
              <Checkbox
                {...attrs}
                required={typeof required === 'boolean' ? required : undefined}
              />
            </RadixRoot>
          </RadixForm.Control>

          {(label || note) && (
            <div>
              {label && (
                <RadixForm.Label asChild>
                  <CheckboxFieldLabel asChild>
                    <div>
                      {label}
                      {required ? '*' : ''}
                    </div>
                  </CheckboxFieldLabel>
                </RadixForm.Label>
              )}
              {note && (
                <CheckboxFieldNote>
                  {note}
                  {!label && required ? '*' : ''}
                </CheckboxFieldNote>
              )}
            </div>
          )}
        </label>

        {(radixFormMessages ?? []).map((props, idx) => (
          <RadixForm.Message key={idx} {...props} asChild>
            <InputFieldError>{props.children}</InputFieldError>
          </RadixForm.Message>
        ))}
      </div>
    </RadixForm.Field>
  );
};

export const CheckboxField = forwardRef<
  HTMLInputElement | null,
  CheckboxFieldProps
>(CheckboxFieldRenderFunction);
